import { useCallback } from 'react';

import { useNoticeBannersStackActions } from '@sg/garnish';

import { type useIdMeVerification } from '../../../../integrations/idme';
import { type useIdMeVerificationContent } from '../useIdMeVerificationContent';

// ─────────────────────────────────────────────────────────────────────────────

export const useIdMeVerificationNotices = (params: Params) => {
  const { content } = params;
  const { cancelledMessage, failedMessage, notVerifiedMessage } = content;
  const { idMeAccountLinkedMessage, sweetgreenAccountLinkedMessage } = content;

  const { push: addNoticeBanner } = useNoticeBannersStackActions();

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const showCancelledNotice = useCallback(() => {
    addNoticeBanner({ id: NOTICE_ID, text: cancelledMessage, palette: 'neutral' }, true);
  }, [addNoticeBanner, cancelledMessage]);

  const showFailedNotice = useCallback(() => {
    addNoticeBanner({ id: NOTICE_ID, text: failedMessage, palette: 'caution' }, true);
  }, [addNoticeBanner, failedMessage]);

  const showNotVerifiedNotice = useCallback(() => {
    addNoticeBanner({ id: NOTICE_ID, text: notVerifiedMessage, palette: 'neutral' }, true);
  }, [addNoticeBanner, notVerifiedMessage]);

  const showLinkConflictNotice = useCallback(
    (verificationResult: VerificationResult) => {
      const text = getLinkConflictMessage({
        verificationResult,
        idMeAccountLinkedMessage,
        sweetgreenAccountLinkedMessage,
        failedMessage,
      });

      addNoticeBanner({ id: NOTICE_ID, text, palette: 'caution' }, true);
    },
    [addNoticeBanner, idMeAccountLinkedMessage, sweetgreenAccountLinkedMessage, failedMessage],
  );

  // ───────────────────────────────────────────────────────────────────────────

  return {
    showCancelledNotice,
    showFailedNotice,
    showNotVerifiedNotice,
    showLinkConflictNotice,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getLinkConflictMessage(params: GetLinkConflictMessageParams) {
  const { verificationResult, idMeAccountLinkedMessage, sweetgreenAccountLinkedMessage } = params;
  const { failedMessage } = params;

  if (verificationResult.__typename === 'CommunityIdentityLinkedToAnotherAccount') {
    return idMeAccountLinkedMessage ?? verificationResult.errorMessage;
  }

  if (verificationResult.__typename === 'CustomerLinkedToDifferentCommunityIdentity') {
    return sweetgreenAccountLinkedMessage ?? verificationResult.errorMessage;
  }

  return failedMessage;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const NOTICE_ID = 'id-me-verification-notice';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  content: Content;
};

type Content = ReturnType<typeof useIdMeVerificationContent>;

type VerificationResult = Parameters<
  Parameters<typeof useIdMeVerification>[0]['onLinkConflict']
>[0];

type GetLinkConflictMessageParams = Pick<
  Content,
  'idMeAccountLinkedMessage' | 'sweetgreenAccountLinkedMessage' | 'failedMessage'
> & {
  verificationResult: VerificationResult;
};
